import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { CreateChancel, ManageActions, NotError } from 'components';
import {
    durationCalcEnums,
    durationCalcList,
    evalModeEnums,
    FindErrorItem,
    FindLoad,
    FindSuccessItem,
    firstTrialResponseEnums,
    formatSeconds,
    goalDataTypeEnums,
    Images,
    masteryDirectionEnums,
    masteryDirectionList,
    masteryEvalTypeEnums,
    masteryEvalTypeList,
    renderGoalValueType,
    toCamelCase,
} from 'utils';
import { httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { FirstTrialCriteriaFields, isFirstTrialActive, isFirstTrialCountValid, isTotalEvalType } from './firstTrialCriteria';

/**
 * Draft-and-save editing for the mastery / baseline / maintenance cards.
 *
 * Every control edits a local draft; nothing is sent until the user presses Save in the CriteriaSaveBar,
 * which only appears while the draft differs from what is saved. Cancel restores the saved values.
 */

export const MASTERY_FIELDS = [
    'automastery',
    'direction',
    'count',
    'evalModeCount',
    'evalType',
    'evalMode',
    'firstTrialOnly',
    'firstTrialResponse',
    'firstTrialCount',
    'durationCalc',
];

export const BASELINE_FIELDS = [
    'autoTransition',
    'direction',
    'count',
    'evalModeCount',
    'evalType',
    'evalMode',
    'firstTrialOnly',
    'firstTrialResponse',
    'firstTrialCount',
    'durationCalc',
];

export const GOAL_MASTERY_FIELDS = ['automastery', 'count'];

export const MAINTENANCE_FIELDS = ['autoMaintenance', 'evalMode', 'evalType', 'firstTrialOnly', 'firstTrialResponse', 'durationCalc'];

/** The maintenance settings the draft edits — the steps are saved one by one, on their own. */
export const pickMaintenanceSettings = (maintenance) =>
    Object.fromEntries(MAINTENANCE_FIELDS.filter((key) => maintenance?.[key] !== undefined).map((key) => [key, maintenance[key]]));

// Values a document saved before a field existed reads back without — treated as equal to the default.
const FIELD_DEFAULTS = {
    evalType: masteryEvalTypeEnums.CONSECUTIVE,
    firstTrialOnly: false,
    firstTrialResponse: firstTrialResponseEnums.CORRECT,
    durationCalc: durationCalcEnums.TOTAL,
    automastery: false,
    autoTransition: false,
    autoMaintenance: false,
};

const normalize = (criteria, key) => {
    const value = criteria?.[key] ?? FIELD_DEFAULTS[key];
    return value === undefined || value === null ? '' : value;
};

export const isCriteriaDirty = (draft, saved, fields) => fields.some((key) => normalize(draft, key) !== normalize(saved, key));

/** A rule can be saved once its threshold and window are filled in. */
export const isRuleValid = (criteria) =>
    typeof criteria?.count === 'number' &&
    criteria?.evalModeCount > 0 &&
    (!criteria?.firstTrialOnly || !isTotalEvalType(criteria) || isFirstTrialCountValid(criteria?.firstTrialCount, criteria?.evalModeCount));

/** Drops half-typed (empty) number fields so the API never receives "" for a number. */
const toRequestBody = (draft) => Object.fromEntries(Object.entries(draft || {}).filter(([, value]) => value !== ''));

/**
 * @param fields     the fields that make the card dirty
 * @param actionType the Redux action the card saves with — its loading / success / error flags drive the bar
 */
export const useCriteriaDraft = ({ fields, actionType }) => {
    const dispatch = useDispatch();
    const [state, setState] = useState({ draft: {}, saved: {} });
    const pendingRef = useRef(null);
    const loader = FindLoad(actionType);
    const success = FindSuccessItem(actionType);
    const error = FindErrorItem(actionType);

    /** Takes the stored value. Unsaved edits survive an unrelated refresh of the goal. */
    const sync = useCallback(
        (value) =>
            setState((prev) =>
                isCriteriaDirty(prev.draft, prev.saved, fields) ? { ...prev, saved: value } : { draft: value, saved: value }
            ),
        [fields]
    );

    const update = useCallback((patch) => setState((prev) => ({ ...prev, draft: { ...prev.draft, ...patch } })), []);

    const reset = useCallback(() => setState((prev) => ({ ...prev, draft: prev.saved })), []);

    /** For values persisted elsewhere (e.g. "use goal settings"): patch both the draft and the saved copy. */
    const applySaved = useCallback(
        (patch) => setState((prev) => ({ draft: { ...prev.draft, ...patch }, saved: { ...prev.saved, ...patch } })),
        []
    );

    const save = (send) => {
        pendingRef.current = state.draft;
        send(toRequestBody(state.draft));
    };

    useEffect(() => {
        if (!pendingRef.current) return;
        if (success) {
            const savedDraft = pendingRef.current;
            pendingRef.current = null;
            setState((prev) => ({ ...prev, saved: savedDraft }));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(actionType));
        } else if (error) {
            pendingRef.current = null;
            const message = typeof error?.error === 'string' ? error.error : 'Could not save the changes. Please try again.';
            toast(<NotError type="error" errMessage={message} />);
            dispatch(httpRequestsOnErrorsActions.removeError(actionType));
        }
    }, [success, error]);

    return {
        draft: state.draft,
        update,
        sync,
        reset,
        applySaved,
        save,
        isDirty: isCriteriaDirty(state.draft, state.saved, fields),
        isSaving: !!loader?.length,
    };
};

/** Footer of a card: shown only while there are unsaved changes (or the save is in flight). */
export const CriteriaSaveBar = ({ dirty, saving, disabled, onSave, onCancel }) => {
    if (!dirty && !saving) return null;

    return (
        <div className="criteria-save-bar">
            <div className="criteria-save-bar-info">
                <span className="criteria-save-bar-dot" />
                <p>{disabled ? 'Fill in all the fields to save' : 'You have unsaved changes'}</p>
            </div>
            <div className="criteria-save-bar-actions">
                <CreateChancel
                    create="Save"
                    chancel="Cancel"
                    onCreate={onSave}
                    onClose={onCancel}
                    loader={saving}
                    disabled={disabled || saving}
                    buttonWidth="96px"
                    height="36px"
                    cancelRight="8px"
                    justifyContent="flex-end"
                />
            </div>
        </div>
    );
};

export const DirectionSelect = ({ value, disabled, onChange }) => (
    <ManageActions
        disabled={disabled}
        btnClass="mastery-direction-box"
        btnTitle={toCamelCase(value)}
        btnIcon={
            value === masteryDirectionEnums.ABOVE ? Images.arrowUpGray : value === masteryDirectionEnums.BELOW ? Images.arrowDown : null
        }
        renderBody={(handleClose) => (
            <div>
                {masteryDirectionList?.map(
                    (i) =>
                        i?.value !== value && (
                            <button
                                key={i?.value}
                                style={{
                                    justifyContent: 'start',
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '8px 0',
                                    width: '100%',
                                }}
                                className="mastery-direction-box"
                                onClick={() => {
                                    onChange(i?.value);
                                    handleClose();
                                }}
                            >
                                <p>{i?.label}</p>
                            </button>
                        )
                )}
            </div>
        )}
    />
);

export const isDurationGoal = (goal) => goal?.dataType === goalDataTypeEnums.DURATION;

// Goals whose session value is a percent — only their thresholds are capped at 100 (mirrors the API).
const PERCENT_VALUE_DATA_TYPES = [
    goalDataTypeEnums.PERCENT,
    goalDataTypeEnums.WHOLE_INTERVAL,
    goalDataTypeEnums.PARTIAL_INTERVAL,
    goalDataTypeEnums.MOMENTARY_TIME_SAMPLING,
];

export const isPercentGoal = (goal) => PERCENT_VALUE_DATA_TYPES.includes(goal?.dataType);

/** DURATION goals: is a session's value the Total (sum) or the Average of its trials' durations. */
export const DurationCalcSelect = ({ value, disabled, onChange }) => (
    <div className="mastery-percent-input-box" style={{ width: 120, padding: '7px 10px' }}>
        <select disabled={disabled} name="durationCalc" value={value || durationCalcEnums.TOTAL} onChange={(e) => onChange(e.target.value)}>
            {durationCalcList.map((i) => (
                <option key={i.value} value={i.value}>
                    {i.label}
                </option>
            ))}
        </select>
    </div>
);

/**
 * The rule sentence of a mastery / baseline card:
 * "<subject> if value is [Above] [90 %] across [5] [Consecutive] [Session]", or its first-trial variant.
 * `onChange` receives a draft patch.
 */
export const CriteriaRuleRow = ({ goal, subject, criteria, disabled, onChange }) => {
    // First trial is judged per session, so Day is not offered while it is on.
    const firstTrial = isFirstTrialActive(goal, criteria);

    return (
        <div className="flex-align-center" style={{ gap: 10, flexWrap: 'wrap', rowGap: 10 }}>
            {firstTrial ? (
                <FirstTrialCriteriaFields
                    label={`${subject} if first response is`}
                    criteria={criteria}
                    disabled={disabled}
                    onChange={onChange}
                />
            ) : (
                <>
                    {isDurationGoal(goal) ? (
                        <>
                            <p className="target-mastery-info">{subject} if</p>
                            <DurationCalcSelect
                                value={criteria?.durationCalc}
                                disabled={disabled}
                                onChange={(durationCalc) => onChange({ durationCalc })}
                            />
                            <p className="target-mastery-info">duration is</p>
                        </>
                    ) : (
                        <p className="target-mastery-info">{subject} if value is</p>
                    )}
                    <DirectionSelect value={criteria?.direction} disabled={disabled} onChange={(direction) => onChange({ direction })} />
                    <div className="mastery-percent-input-box">
                        <input
                            disabled={disabled}
                            type="number"
                            name="count"
                            value={criteria?.count ?? ''}
                            onChange={(e) => {
                                const raw = e.target.value;
                                let value = raw === '' ? '' : +raw;
                                if (typeof value === 'number' && value < 0) value = 0;
                                if (typeof value === 'number' && value > 100 && isPercentGoal(goal)) value = 100;
                                onChange({ count: value });
                            }}
                        />
                        <p>{renderGoalValueType(goal)}</p>
                    </div>
                    {/* Durations are entered in seconds — spell out larger values (150 s -> 2m 30s). */}
                    {isDurationGoal(goal) && criteria?.count >= 60 && (
                        <p className="target-mastery-info" style={{ color: '#667085', fontSize: 14 }}>
                            ({formatSeconds(criteria.count)})
                        </p>
                    )}
                </>
            )}

            <p className="target-mastery-info">across</p>

            <div className="mastery-percent-input-box" style={{ width: '78px' }}>
                <input
                    disabled={disabled}
                    type="number"
                    name="evalModeCount"
                    value={criteria?.evalModeCount ?? ''}
                    onChange={(e) => onChange({ evalModeCount: +e.target.value > 0 ? +e.target.value : '' })}
                />
            </div>

            {/* Consecutive = every session/day must meet the criteria; Total = their average (or count of first responses) must. */}
            <div className="mastery-percent-input-box mastery-eval-type-box">
                <select
                    disabled={disabled}
                    name="evalType"
                    value={criteria?.evalType || masteryEvalTypeEnums.CONSECUTIVE}
                    onChange={(e) => onChange({ evalType: e.target.value })}
                >
                    {masteryEvalTypeList?.map((i) => (
                        <option key={i?.value} value={i?.value}>
                            {i?.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="mastery-percent-input-box" style={{ width: '110px' }}>
                <select
                    disabled={disabled}
                    name="evalMode"
                    value={criteria?.evalMode || evalModeEnums.BY_SESSION}
                    onChange={(e) => onChange({ evalMode: e.target.value })}
                >
                    <option value={evalModeEnums.BY_DAY} disabled={firstTrial}>
                        Day
                    </option>
                    <option value={evalModeEnums.BY_SESSION}>Session</option>
                </select>
            </div>
        </div>
    );
};
